import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Mail, MapPin, Phone, Check, AlertCircle, Info, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { useSeo } from "../hooks/useSeo";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TERMS_VERSION = "2026-02-01";
const TERMS_PATH = "/algemene-voorwaarden";

function renderWithLinks(template, mapping) {
  // Splits a template containing placeholders like {privacy} / {terms} into React nodes.
  const tokens = Object.keys(mapping);
  const re = new RegExp(`(${tokens.map((t) => `\\{${t}\\}`).join("|")})`, "g");
  const parts = template.split(re);
  return parts.map((p, i) => {
    const key = p.replace(/[{}]/g, "");
    if (mapping[key]) return <span key={i}>{mapping[key]}</span>;
    return <span key={i}>{p}</span>;
  });
}

export default function Contact() {
  const { t, lang } = useI18n();
  const { calendly_url: calendlyUrl } = usePublicConfig();
  const [searchParams] = useSearchParams();
  const isIntake = searchParams.get("intent") === "intake";

  useSeo({
    title: isIntake ? `${t.contact.intake.eyebrow} — Tip Top Property` : t.seo.contact.title,
    description: isIntake ? t.contact.intake.lead : t.seo.contact.description,
    path: "/contact",
    lang,
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    property_type: "",
    budget: "",
    city: "",
    timing: "",
    message: "",
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  // Cache last successful submission so we can prefill Calendly on the success state
  const [submitted, setSubmitted] = useState(null);

  // Build a Calendly URL with prefilled name + email (Calendly accepts standard ?name & ?email)
  const calendlyHref = useMemo(() => {
    if (!calendlyUrl) return "";
    const source = submitted || form;
    const params = new URLSearchParams();
    if (source.name?.trim()) params.set("name", source.name.trim());
    if (source.email?.trim()) params.set("email", source.email.trim());
    const qs = params.toString();
    if (!qs) return calendlyUrl;
    return `${calendlyUrl}${calendlyUrl.includes("?") ? "&" : "?"}${qs}`;
  }, [calendlyUrl, form.name, form.email, submitted]);

  // Pre-fill from query string (e.g. coming from a case study page)
  useEffect(() => {
    const caseRef = searchParams.get("case");
    const city = searchParams.get("city");
    const type = searchParams.get("type");
    if (!caseRef && !city && !type) return;
    setForm((f) => ({
      ...f,
      city: city || f.city,
      message:
        f.message ||
        (caseRef ? `${t.cases.contextPrefix}: ${caseRef}` : `${t.cases.contextPrefix}: ${city || ""} ${type || ""}`.trim()),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const termsAbsoluteUrl = useMemo(() => {
    if (typeof window === "undefined") return TERMS_PATH;
    return `${window.location.origin}${TERMS_PATH}`;
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t.contact.error);
      return;
    }
    if (isIntake && !termsAccepted) {
      setTermsError(true);
      toast.error(t.contact.intake.clickwrapError);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        language: lang,
        request_type: isIntake ? "intake" : "contact",
        lead_source: isIntake ? "intake_form" : "contact_form",
      };
      if (isIntake) {
        payload.terms_accepted = true;
        payload.terms_accepted_at = new Date().toISOString();
        payload.terms_version = TERMS_VERSION;
        payload.terms_language = lang;
        payload.terms_url = termsAbsoluteUrl;
      }
      const res = await axios.post(`${API}/leads`, payload);
      setSubmitted({
        name: form.name,
        email: form.email,
        update_token: res?.data?.update_token,
        request_type: res?.data?.request_type,
      });
      setDone(true);
      toast.success(isIntake ? t.contact.intake.success : t.contact.success);
      setForm({ name: "", email: "", phone: "", country: "", property_type: "", budget: "", city: "", timing: "", message: "" });
      setTermsAccepted(false);
      setTermsError(false);
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const heading = isIntake
    ? { eyebrow: t.contact.intake.eyebrow, title: t.contact.intake.title, lead: t.contact.intake.lead }
    : { eyebrow: t.contact.eyebrow, title: t.contact.title, lead: t.contact.lead };

  return (
    <section className="container-wide py-16 md:py-24" data-testid={isIntake ? "contact-page-intake" : "contact-page-general"}>
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="eyebrow">{heading.eyebrow}</p>
          <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium text-foreground">{heading.title}</h1>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">{heading.lead}</p>

          <div className="mt-10 space-y-5 text-sm">
            <div className="flex items-start gap-3">
              <span className="h-9 w-9 grid place-items-center rounded-md bg-secondary text-accent">
                <Mail className="h-4 w-4" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">E-mail</p>
                <p className="text-foreground">hello@tiptopproperty.eu</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="h-9 w-9 grid place-items-center rounded-md bg-secondary text-accent">
                <Phone className="h-4 w-4" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Telefoon</p>
                <p className="text-foreground">+31 85 115 57 54</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="h-9 w-9 grid place-items-center rounded-md bg-secondary text-accent">
                <MapPin className="h-4 w-4" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.common.benelux}</p>
                <p className="text-foreground">Robert Schumandomein 2, 6229ES Maastricht</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div className="card-soft p-10 text-center" data-testid="contact-success">
              <div className="mx-auto h-12 w-12 rounded-full bg-accent/15 grid place-items-center text-accent">
                <Check className="h-5 w-5" />
              </div>
              <h2 className="mt-5 text-2xl text-foreground">{isIntake ? t.contact.intake.success : t.contact.success}</h2>
              <p className="mt-3 text-muted-foreground">{t.home.ctaSub}</p>
              {isIntake && calendlyUrl && (
                <div className="mt-7 pt-7 border-t border-border">
                  <p className="text-xs uppercase tracking-[0.22em] font-bold text-accent">
                    {t.contact.intake.calendlySuccessLabel}
                  </p>
                  <a
                    href={calendlyHref}
                    target="_blank"
                    rel="noopener"
                    data-testid="calendly-success-cta"
                    className="btn-primary mt-4 justify-center inline-flex"
                  >
                    <CalendarClock className="h-4 w-4" />
                    {t.contact.intake.calendlySuccessCta}
                  </a>
                </div>
              )}
              {isIntake && submitted?.update_token && (
                <div className="mt-5">
                  <Link
                    to={`/intake/${submitted.update_token}`}
                    data-testid="self-service-link"
                    className="inline-flex items-center gap-2 text-sm text-foreground/80 hover:text-accent transition-colors link-underline"
                  >
                    <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-bold mr-1">
                      {t.contact.intake.selfServiceLabel}
                    </span>
                    {t.contact.intake.selfServiceCta}
                  </Link>
                </div>
              )}
              <button
                type="button"
                onClick={() => setDone(false)}
                className="mt-6 btn-ghost"
                data-testid="contact-reset"
              >
                {t.common.contact_us}
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="card-soft p-6 md:p-8 grid sm:grid-cols-2 gap-5" data-testid="contact-form">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.contact.fields.name}</Label>
                <Input value={form.name} onChange={set("name")} placeholder={t.contact.placeholders.name} required data-testid="input-name" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.contact.fields.email}</Label>
                <Input type="email" value={form.email} onChange={set("email")} placeholder={t.contact.placeholders.email} required data-testid="input-email" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.contact.fields.phone}</Label>
                <Input value={form.phone} onChange={set("phone")} placeholder={t.contact.placeholders.phone} data-testid="input-phone" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.contact.fields.country}</Label>
                <Input value={form.country} onChange={set("country")} placeholder={t.contact.placeholders.country} data-testid="input-country" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.contact.fields.city}</Label>
                <Input value={form.city} onChange={set("city")} placeholder={t.contact.placeholders.city} data-testid="input-city" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.contact.fields.property_type}</Label>
                <Select value={form.property_type} onValueChange={(v) => setForm((f) => ({ ...f, property_type: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="select-property-type">
                    <SelectValue placeholder={t.contact.placeholders.property_type_select} />
                  </SelectTrigger>
                  <SelectContent>
                    {t.contact.propertyTypes.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`pt-${p}`}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.contact.fields.budget}</Label>
                <Select value={form.budget} onValueChange={(v) => setForm((f) => ({ ...f, budget: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="select-budget">
                    <SelectValue placeholder={t.contact.placeholders.budget_select} />
                  </SelectTrigger>
                  <SelectContent>
                    {t.contact.budgets.map((b) => (
                      <SelectItem key={b} value={b} data-testid={`b-${b}`}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isIntake && (
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.contact.timingLabel}</Label>
                  <Select value={form.timing} onValueChange={(v) => setForm((f) => ({ ...f, timing: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="select-timing">
                      <SelectValue placeholder={t.contact.timingPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {t.contact.timings.map((tt) => (
                        <SelectItem key={tt.value} value={tt.value} data-testid={`timing-${tt.value}`}>{tt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.contact.fields.message}</Label>
                <Textarea rows={5} value={form.message} onChange={set("message")} placeholder={t.contact.placeholders.message} data-testid="input-message" className="mt-1.5" />
              </div>

              {isIntake ? (
                <>
                  {/* Contractual information — compact, premium */}
                  <div
                    className="sm:col-span-2 mt-1 rounded-md border border-border bg-secondary/40 p-5"
                    data-testid="intake-contract-info"
                  >
                    <p className="text-xs uppercase tracking-[0.22em] font-bold text-accent">
                      {t.contact.intake.contractInfoTitle}
                    </p>
                    <ul className="mt-3 space-y-2">
                      {t.contact.intake.contractInfoItems.map((item, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/85 leading-relaxed">
                          <span className="mt-1.5 h-1 w-1 rounded-full bg-accent flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Advance note */}
                  <div
                    className="sm:col-span-2 flex items-start gap-2.5 rounded-md border border-accent/30 bg-accent/5 px-4 py-3 text-sm text-foreground/90"
                    data-testid="intake-advance-note"
                  >
                    <Info className="h-4 w-4 mt-0.5 text-accent flex-shrink-0" />
                    <p className="leading-relaxed">{t.contact.intake.advanceNote}</p>
                  </div>

                  {/* Clickwrap checkbox */}
                  <div className="sm:col-span-2" data-testid="intake-clickwrap">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => {
                          setTermsAccepted(e.target.checked);
                          if (e.target.checked) setTermsError(false);
                        }}
                        data-testid="intake-clickwrap-checkbox"
                        aria-invalid={termsError}
                        aria-describedby={termsError ? "intake-clickwrap-error" : undefined}
                        className={`mt-1 h-4 w-4 cursor-pointer appearance-none rounded border bg-background transition-all
                          ${termsError ? "border-destructive ring-2 ring-destructive/20" : "border-border group-hover:border-accent"}
                          checked:bg-accent checked:border-accent
                          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background
                          relative
                          checked:after:content-['✓'] checked:after:text-primary-foreground checked:after:text-[11px] checked:after:font-bold
                          checked:after:absolute checked:after:inset-0 checked:after:grid checked:after:place-items-center`}
                      />
                      <span className="text-sm text-foreground/90 leading-relaxed">
                        {renderWithLinks(t.contact.intake.clickwrap, {
                          terms: (
                            <Link
                              to={TERMS_PATH}
                              target="_blank"
                              rel="noopener"
                              className="text-foreground underline underline-offset-4 hover:text-accent transition-colors"
                              onClick={(e) => e.stopPropagation()}
                              data-testid="intake-clickwrap-terms-link"
                            >
                              {t.contact.legalTermsLabel}
                            </Link>
                          ),
                        })}
                      </span>
                    </label>
                    {termsError && (
                      <p
                        id="intake-clickwrap-error"
                        className="mt-2 flex items-center gap-1.5 text-xs text-destructive"
                        data-testid="intake-clickwrap-error"
                      >
                        <AlertCircle className="h-3.5 w-3.5" />
                        {t.contact.intake.clickwrapError}
                      </p>
                    )}
                  </div>

                  {/* Privacy microcopy (kept subtle) */}
                  <div className="sm:col-span-2">
                    <p className="text-xs text-muted-foreground leading-relaxed" data-testid="legal-microcopy">
                      {renderWithLinks(t.contact.legalMicrocopy, {
                        privacy: (
                          <Link to="/privacy" className="text-foreground underline underline-offset-4 hover:text-accent transition-colors">
                            {t.contact.legalPrivacyLabel}
                          </Link>
                        ),
                        terms: (
                          <Link to={TERMS_PATH} className="text-foreground underline underline-offset-4 hover:text-accent transition-colors">
                            {t.contact.legalTermsLabel}
                          </Link>
                        ),
                      })}
                    </p>
                  </div>
                </>
              ) : (
                <div className="sm:col-span-2 pt-2">
                  <p className="text-xs text-muted-foreground leading-relaxed" data-testid="legal-microcopy">
                    {renderWithLinks(t.contact.legalMicrocopy, {
                      privacy: (
                        <Link to="/privacy" className="text-foreground underline underline-offset-4 hover:text-accent transition-colors">
                          {t.contact.legalPrivacyLabel}
                        </Link>
                      ),
                      terms: (
                        <Link to={TERMS_PATH} className="text-foreground underline underline-offset-4 hover:text-accent transition-colors">
                          {t.contact.legalTermsLabel}
                        </Link>
                      ),
                    })}
                  </p>
                </div>
              )}

              <div className="sm:col-span-2 flex items-center justify-between gap-4 pt-2 border-t border-border">
                <p className="text-xs text-muted-foreground max-w-md">
                  {t.common.benelux}
                </p>
                <div className="flex items-center gap-2">
                  {isIntake && calendlyUrl && (
                    <a
                      href={calendlyHref}
                      target="_blank"
                      rel="noopener"
                      data-testid="calendly-secondary-cta"
                      title={t.contact.intake.calendlyLabel}
                      className="btn-ghost hidden sm:inline-flex"
                    >
                      <CalendarClock className="h-4 w-4" />
                      {t.contact.intake.calendlyCta}
                    </a>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-button"
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? "…" : isIntake ? t.contact.intake.submit : t.contact.submit}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
